import { ui } from "@/strings";

interface HighlightProgressBarProps {
  resolvedCount: number;
  totalCount: number;
}

/**
 * Shows progress of highlight resolution as "X of Y resolved" with a visual bar.
 * Used in the student writing view to track feedback resolution progress.
 */
export function HighlightProgressBar({
  resolvedCount,
  totalCount,
}: HighlightProgressBarProps) {
  if (totalCount === 0) {
    return null;
  }

  const progressPercent = Math.round((resolvedCount / totalCount) * 100);
  const isComplete = resolvedCount === totalCount;

  return (
    <div className="flex flex-col gap-1.5 px-3 py-2 border-b border-theme-primary bg-white">
      <div className="flex items-center justify-between text-xs">
        <span className="text-theme-secondary font-medium">
          {ui.highlightNavigation.progressLabel}
        </span>
        <span
          className={`font-semibold ${isComplete ? "text-semantic-success" : "text-theme-secondary"}`}
        >
          {ui.highlightNavigation.progressCount(resolvedCount, totalCount)}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-border overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            isComplete ? "bg-semantic-success" : "bg-primary"
          }`}
          style={{ width: `${progressPercent}%` }}
          role="progressbar"
          aria-valuenow={resolvedCount}
          aria-valuemin={0}
          aria-valuemax={totalCount}
          aria-label={ui.highlightNavigation.progressLabel}
        />
      </div>
    </div>
  );
}
